import { zodResolver } from "@hookform/resolvers/zod"
import { CalendarIcon, Mars, Venus } from "lucide-react"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { Button } from "@/shared/components/ui/button"
import { Calendar } from "@/shared/components/ui/calendar"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form"
import { Input } from "@/shared/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover"
import { cn } from "@/shared/lib/utils"
import { formatLocalIsoDate, parseLocalIsoDate } from "@/shared/utils/time"
import { type ChildFormInput, type ChildFormValues, childFormSchema } from "./children.schemas"

interface ChildFormProps {
  defaultValues: ChildFormInput
  onSubmit: (values: ChildFormValues) => void
  isPending: boolean
  submitLabel: string
}

const DATE_FORMAT = new Intl.DateTimeFormat("sv-SE", { dateStyle: "long" })

export function ChildForm({ defaultValues, onSubmit, isPending, submitLabel }: ChildFormProps) {
  const form = useForm<ChildFormInput, unknown, ChildFormValues>({
    resolver: zodResolver(childFormSchema),
    defaultValues,
  })
  const [dateOpen, setDateOpen] = useState(false)

  const today = new Date()

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Namn</FormLabel>
              <FormControl>
                <Input autoComplete="off" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="birthDate"
          render={({ field }) => {
            const selected = field.value ? parseLocalIsoDate(field.value) : undefined
            return (
              <FormItem className="flex flex-col">
                <FormLabel>Födelsedatum</FormLabel>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        type="button"
                        variant="outline"
                        className={cn(
                          "justify-start font-normal",
                          !selected && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon />
                        {selected ? DATE_FORMAT.format(selected) : "Välj datum"}
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selected}
                      defaultMonth={selected ?? today}
                      captionLayout="dropdown"
                      startMonth={new Date(today.getFullYear() - 8, 0)}
                      endMonth={today}
                      disabled={{ after: today }}
                      onSelect={(date) => {
                        field.onChange(date ? formatLocalIsoDate(date) : "")
                        setDateOpen(false)
                      }}
                      autoFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )
          }}
        />
        <FormField
          control={form.control}
          name="sex"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Kön</FormLabel>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={field.value === "boy" ? "default" : "outline"}
                  className="flex-1"
                  onClick={() => field.onChange("boy")}
                >
                  <Mars />
                  Pojke
                </Button>
                <Button
                  type="button"
                  variant={field.value === "girl" ? "default" : "outline"}
                  className="flex-1"
                  onClick={() => field.onChange("girl")}
                >
                  <Venus />
                  Flicka
                </Button>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" className="mt-2 w-full" isLoading={isPending}>
          {submitLabel}
        </Button>
      </form>
    </Form>
  )
}
